import { useEffect, useRef, useCallback, useState } from 'react';

// Типы для MediaPipe Selfie Segmentation
interface SegmentationResults {
  image: HTMLCanvasElement | HTMLImageElement | ImageBitmap;
  segmentationMask: HTMLCanvasElement | HTMLImageElement | ImageBitmap;
}

interface SelfieSegmentationInstance {
  close(): Promise<void>;
  onResults(listener: (results: SegmentationResults) => void): void;
  initialize(): Promise<void>;
  reset(): void;
  send(inputs: { image: HTMLVideoElement }): Promise<void>;
  setOptions(options: { selfieMode?: boolean; modelSelection?: number }): void;
}

interface SelfieSegmentationConstructor {
  new (config: { locateFile: (file: string) => string }): SelfieSegmentationInstance;
}

declare global {
  interface Window {
    SelfieSegmentation?: SelfieSegmentationConstructor;
  }
}

// Загрузка скрипта MediaPipe
let scriptLoadPromise: Promise<void> | null = null;
function loadMediaPipeScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise;
  
  if (window.SelfieSegmentation) {
    return Promise.resolve();
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load MediaPipe script'));
    document.head.appendChild(script);
  });
  
  return scriptLoadPromise;
}

interface UseBackgroundBlurOptions {
  enabled: boolean;
  blurAmount?: number;
}

interface UseBackgroundBlurResult {
  processedStream: MediaStream | null;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  isProcessing: boolean;
  error: string | null;
}

export const useBackgroundBlur = (
  sourceStream: MediaStream | null,
  options: UseBackgroundBlurOptions
): UseBackgroundBlurResult => {
  const { enabled, blurAmount = 15 } = options;
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const segmentationRef = useRef<SelfieSegmentationInstance | null>(null);
  const animationRef = useRef<number | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const isRunningRef = useRef<boolean>(false);
  
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopProcessing = useCallback(() => {
    isRunningRef.current = false;
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current = null;
    }
    if (segmentationRef.current) {
      segmentationRef.current.close();
      segmentationRef.current = null;
    }
    if (processedStreamRef.current) {
      processedStreamRef.current.getTracks().forEach(track => track.stop());
      processedStreamRef.current = null;
    }
    setProcessedStream(null);
    setIsProcessing(false);
  }, []);

  const startProcessing = useCallback(async () => {
    if (!sourceStream || !enabled) {
      stopProcessing();
      return;
    }

    const videoTrack = sourceStream.getVideoTracks()[0];
    if (!videoTrack) {
      setError('Нет видео трека для обработки');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      isRunningRef.current = true;

      // Создаем скрытый video элемент
      const video = document.createElement('video');
      video.srcObject = sourceStream;
      video.muted = true;
      video.playsInline = true;
      await video.play();
      videoRef.current = video;

      // Получаем canvas
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas не найден');
      }

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        throw new Error('Не удалось получить контекст canvas');
      }

      // Устанавливаем размеры canvas
      const settings = videoTrack.getSettings();
      canvas.width = settings.width || 640;
      canvas.height = settings.height || 480;

      // Создаем offscreen canvas для blur эффекта
      const blurCanvas = document.createElement('canvas');
      blurCanvas.width = canvas.width;
      blurCanvas.height = canvas.height;
      const blurCtx = blurCanvas.getContext('2d');

      if (!blurCtx) {
        throw new Error('Не удалось создать blur canvas');
      }

      // Загружаем MediaPipe Selfie Segmentation через CDN
      await loadMediaPipeScript();
      
      if (!window.SelfieSegmentation) {
        throw new Error('MediaPipe SelfieSegmentation не загружен');
      }
      
      const selfieSegmentation = new window.SelfieSegmentation({
        locateFile: (file: string) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`;
        }
      });

      selfieSegmentation.setOptions({
        modelSelection: 1, // 0 = general, 1 = landscape (faster)
        selfieMode: true,
      });

      // Обработчик результатов сегментации
      selfieSegmentation.onResults((results: SegmentationResults) => {
        if (!ctx || !isRunningRef.current) return;

        // Очищаем canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Рисуем размытый фон
        ctx.save();
        blurCtx.filter = `blur(${blurAmount}px)`;
        blurCtx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
        blurCtx.filter = 'none';
        ctx.drawImage(blurCanvas, 0, 0);
        ctx.restore();

        // Используем маску сегментации для вырезания человека
        ctx.save();
        ctx.globalCompositeOperation = 'destination-out';
        ctx.drawImage(results.segmentationMask, 0, 0, canvas.width, canvas.height);
        ctx.restore();

        // Рисуем четкого человека поверх
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.drawImage(results.image, 0, 0, canvas.width, canvas.height);
        ctx.restore();
      });

      await selfieSegmentation.initialize();
      segmentationRef.current = selfieSegmentation;

      // Функция обработки кадров
      const processFrame = async () => {
        if (!isRunningRef.current || !videoRef.current || !segmentationRef.current) {
          return;
        }

        if (videoRef.current.readyState >= 2) {
          await segmentationRef.current.send({ image: videoRef.current });
        }

        animationRef.current = requestAnimationFrame(processFrame);
      };

      // Запускаем обработку
      processFrame();

      // Создаем MediaStream из canvas
      const canvasStream = canvas.captureStream(30);
      
      // Добавляем аудио треки из оригинального стрима
      const audioTracks = sourceStream.getAudioTracks();
      audioTracks.forEach(track => {
        canvasStream.addTrack(track.clone());
      });

      processedStreamRef.current = canvasStream;
      setProcessedStream(canvasStream);

    } catch (err: any) {
      console.error('Background blur error:', err);
      setError(err.message || 'Ошибка при обработке видео');
      setIsProcessing(false);
      isRunningRef.current = false;
    }
  }, [sourceStream, enabled, blurAmount, stopProcessing]);

  useEffect(() => {
    if (enabled && sourceStream) {
      startProcessing();
    } else {
      stopProcessing();
    }

    return () => {
      stopProcessing();
    };
  }, [enabled, sourceStream, startProcessing, stopProcessing]);

  return {
    processedStream,
    canvasRef,
    isProcessing,
    error
  };
};
